import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import Spinner from '../Spinner';
import { useApi } from '../../api/ApiProvider';
import Api from '../../axiosApi/api';
import { useToastMessageQueue } from '../../components/ToastMessages/ToastMessageProvider';
import intl from 'components/i18n/ReactIntlWrapper';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import { handleAPIError } from 'common/errorHandler';
import { Typography, Box, Link as MuiLink, Chip, Button, useTheme, Card } from '@mui/material';
import { DataGrid, GridColDef, GridInitialState } from '@mui/x-data-grid';
import { DEFAULT_PAGINATION, useSearch } from 'hooks/useSearch';
import { WORKFLOW_MODULES } from 'common/constants';
import { getUserEntity } from 'common/UserEntityProvider';
import WorkflowActionsBar from '../Workflow/WorkflowActionsBar';
import { WorkflowProvider } from '../Workflow/WorkflowContext';
import DefaultActions from 'components/DataGrid/DefaultActions';
import AddIcon from '@mui/icons-material/Add';
import { formatDateTimeOff } from '../../common/utils';
import TimeOffNavbar from './TimeOffNavbar';
import TimeOffStockCarrousel from './TimeOffStockCarrousel';
import { EmployeeTimeOffRequestDto } from 'axiosApi/models/employee-time-off-request-dto';
import { useEmployeeTimeOffRegime, useTimeOffTypes } from 'store/registry/useTimeOffData';

export type TimeOffInput = {
  employee: string | null;
  date: string | null;
  queryTerm?: string | null;
  currentPage?: number;
  sortValue?: string | null;
  orderValue?: string | null;
  idWorkflowState: number | null;
  IdEmployeeTimeOffType: number | null;
  includeProcessed?: boolean;
};

export type SearchResult = {
  totalRecords: number | null;
  queryResult: any;
};

const TimeOff = ({ HRView = false }) => {
  const [input, setInput] = useState<TimeOffInput | null>({
    employee: '',
    date: '',
    queryTerm: '',
    currentPage: 1,
    sortValue: 'employee',
    orderValue: 'asc',
    idWorkflowState: null,
    IdEmployeeTimeOffType: null,
    includeProcessed: false,
  });

  const theme = useTheme();

  const [errors, setErrors] = useState(null);
  const api: Api = useApi();

  const toast = useToastMessageQueue();

  const navigate = useNavigate();

  const entityType = getUserEntity();

  // Use cached hooks for time off data
  const { 
    data: employeeTimeOffData, 
    isLoading: timeOffDataLoading, 
    regimeId 
  } = useEmployeeTimeOffRegime(entityType?.entityId || 0, !!entityType?.entityId);

  const { data: timeOffTypesData } = useTimeOffTypes(regimeId, HRView, !!regimeId || HRView);

  // Extract data from hooks
  const timeOffStocks = employeeTimeOffData[0]?.employeeTimeOffStocks || [];
  const employeeRegimeTimeOffTypes = employeeTimeOffData[0]?.employeeRegimeTimeOffTypes || [];
  const requestType = { queryResult: timeOffTypesData };

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortField, setSortField] = useState<string>('state');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const INITIAL_STATE: GridInitialState = {
    pagination: {
      paginationModel: {
        page: DEFAULT_PAGINATION.pageNum,
        pageSize: DEFAULT_PAGINATION.pageSize,
      },
    },
    sorting: {
      sortModel: [
        {
          field: 'employee',
          sort: 'asc',
        },
      ],
    },
  };

  const handleTimeOffDetail = (HRView: boolean, id) => {
    if (HRView) {
      navigate(`/timeOffRequestHR/detail/${id}`);
    } else {
      navigate(`/timeOffRequest/detail/${id}`);
    }
  };

  const handleSave = async (HRView) => {
    toast.success({ message: intl.get('calendarEvent.toast.success.handleSave') });
    setTimeout(() => {
      reFetchData();
    }, 2000);
  };

  const columns: GridColDef[] = [
    {
      field: 'employee',
      headerName: intl.get('timeOff.table.thead.employee'),
      width: 250,
      flex: 1,
      renderCell: (params) => <>{`${params.row.employeeLastName} ${params.row.employeeName}`}</>,
    },
    {
      field: 'state',
      headerName: intl.get('timeOff.table.thead.status'),
      width: 250,
      flex: 1,
      renderCell: (params) => (
        <Chip
          variant="outlined"
          className="px-4 py-2"
          label={
            <Typography className="statusLabel">{intl.get(params.row.workFlowLabelKey)}</Typography>
          }
          sx={{
            backgroundColor: params.row.workflowStateColor ? params.row.workflowStateColor : '#fff',
            color: theme.palette.getContrastText(
              params.row.workflowStateColor ? params.row.workflowStateColor : '#fff'
            ),
            borderColor: params.row.workflowStateColor ? params.row.workflowStateColor : '#fff',
          }}
        />
      ),
    },
    {
      field: 'type',
      headerName: intl.get('timeOff.table.thead.request'),
      width: 250,
      flex: 1,
      renderCell: (params) => <>{params.row.timeOffTypeName}</>,
      },
	    {
      field: 'from',
      headerName: intl.get('timeOff.table.thead.dateRange'),
      width: 250,
      flex: 1,
      renderCell: (params) => <>{`${params.row.from} - ${params.row.to}`}</>,
    },
    {
      field: 'actions',
      headerName: intl.get('timeOff.table.thead.actions'),
      align: 'center',
      sortable: false,
      headerAlign: 'center',
      width: 150,
      renderCell: (params) => (
        <WorkflowProvider moduleId={2}>
          <WorkflowActionsBar
            viewMode="icons"
            onActionClick={handleSave}
            id={params.row.id}
            stateId={params.row.idWorkflowState}
            loading={loading}
          />
        </WorkflowProvider>
      ),
    },
    {
      field: 'detail',
      headerName: intl.get('timeOff.table.thead.detail'),
      width: 150,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          <DefaultActions
            row={params.row}
            entityType={entityType.entityType}
            onView={() => handleTimeOffDetail(HRView, params.row.id)}
            identifier="ui.timeoff.main.owner-gate"
          />
        </Box>
      ),
    },
  ];

  // fetchRequestType is now handled by useTimeOffTypes hook

  const hrApiPromise = (
    searchTerm: string,
    pageNum: number,
    pageSize: number,
    sort: string,
    order: string,
    f?: Array<string>
  ) => {
    if (input?.idWorkflowState) {
      f = [`state:${input.idWorkflowState}`];
    }
    if (input?.IdEmployeeTimeOffType) {
      f = f || [];
      f.push(`type:${input.IdEmployeeTimeOffType}`);
    }
    if (input?.includeProcessed) {
      f = f || [];
      f.push(`includeprocessed:true`);
    }
    return api.timeoffApi.apiVversionEmployeeTimeOffRequestSearchGet(
      "1", pageNum, pageSize, searchTerm, sort, order, 0, undefined, f
    );
  };

  const userApiPromise = (
    searchTerm: string,
    pageNum: number,
    pageSize: number,
    sort: string,
    order: string,
    f?: Array<string>
  ) => {
    if (input?.idWorkflowState) {
      f = [`state:${input.idWorkflowState}`];
    }
    if (input?.IdEmployeeTimeOffType) {
      f = f || [];
      f.push(`type:${input.IdEmployeeTimeOffType}`);
    }
    if (input?.includeProcessed) {
      f = f || [];
      f.push(`includeprocessed:true`);
    }
    return api.timeoffApi.apiVversionEmployeeTimeOffRequestSearchMineGet(
      "1", pageNum, pageSize, sort, order, f
    )};

  const processError = (error: any) => {
    handleAPIError(error, toast, errors);
    setErrors({ ...errors });
  };

  const {
    data: rawData,
    loading,
    onPaginationModelChange,
    handleSortModelChange: originalHandleSortModelChange,
    setFilter,
    reFetchData,
    paginationModel,
  } = useSearch<EmployeeTimeOffRequestDto>({
    apiPromise: HRView ? hrApiPromise : userApiPromise,
    searchTerm: searchTerm,
    defaultSort: { sort: sortField, order: sortOrder },
    onError: processError,
    initialFilter: HRView ? ['hrview'] : ['userview'],
    pageKey: 'timeOff',
  });

  const handleSortModelChange = (sortModel) => {
    if (sortModel && sortModel.length > 0) {
      setSortField(sortModel[0].field);
      setSortOrder(sortModel[0].sort);
    }
    originalHandleSortModelChange(sortModel);
  };

  const data = React.useMemo(() => {
    if (rawData?.queryResult) {
      const extraFilter = rawData.queryResult.filter(
        (timeoff) =>
          (input?.idWorkflowState ? timeoff.idWorkflowState === input.idWorkflowState : true) &&
          (input?.IdEmployeeTimeOffType
            ? timeoff.timeOffTypeId === input.IdEmployeeTimeOffType
            : true)
      );
      const formattedDates = extraFilter.map((req) => {
        return {
          ...req,
          from: formatDateTimeOff(req.from),
          to: formatDateTimeOff(req.to),
        };
      });
      return {
        queryResult: formattedDates,
        totalRecords: rawData.totalRecords,
      };
    }
    return {
      queryResult: [],
      totalRecords: 0,
    };
  }, [rawData, HRView]);



  useEffect(() => {
    setFilter(HRView ? ['hrview'] : ['userview']);
    setInput({
      ...input,
      idWorkflowState: null,
      IdEmployeeTimeOffType: null,
    });
  }, [HRView, setFilter]);

  const onSearch = (queryString) => {
    setErrors([]);
    setSearchTerm(queryString);
    reFetchData();
  };

  const handleCalendarView = (HRView: boolean) => {
    if (HRView) {
      navigate('/CalendarHR');
    } else {
      navigate('/Calendar');
    }
  };
  return (
    <Card sx={{ m: 2 }}>
      <Box sx={{ p: 3 }}>
        <Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>
          {HRView ? intl.get('timeOff.header.HR') : intl.get('timeOff.header')}
        </Typography>
        <MuiLink
          component="button"
          variant="body2"
          color="secondary"
          onClick={() => handleCalendarView(HRView)}
          sx={{ display: 'flex', alignItems: 'center', fontSize: '1rem' }}
        >
          {intl.get('timeOff.calendar')}
          <CalendarTodayOutlinedIcon style={{ marginLeft: 4, marginBottom: 2 }} />
        </MuiLink>
        <Box>
          {HRView ? null : !HRView && timeOffStocks.length > 0 ? (
            <TimeOffStockCarrousel
              timeOffStocks={timeOffStocks}
              employeeRegimeTimeOffTypes={employeeRegimeTimeOffTypes}
              loading={timeOffDataLoading}
            /> ) : (<Box sx={{
              textAlign: 'center',
              py: 8,
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" color="text.secondary" mb={2}>
              {intl.get('timeOff.messages.noRegimeAssigned')}
            </Typography>
          </Box>
          )}
        </Box>

        <Box sx={{ p: 3 }}>
          <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} mb={2}>
            <WorkflowProvider moduleId={WORKFLOW_MODULES.TIME_OFF}>
              <TimeOffNavbar
                requestType={requestType?.queryResult}
                onSearch={onSearch}
                HRView={HRView}
                setInput={setInput}
                input={input}
              />
            </WorkflowProvider>
            <Button
              disabled={!HRView && timeOffStocks.length === 0}
              sx={{ height: 40 }}
              onClick={() =>
                HRView ? navigate(`/timeOff/requestHR`) : navigate(`/timeOff/request`)
              }
            >
              <AddIcon /> {intl.get('timeOff.new') ? intl.get('timeOff.new') : intl.get('button.new')}
            </Button>
          </Box>
          <Box style={{ width: '100%', display: 'flex' }}>
            {loading && <Spinner />}
            <DataGrid
              rows={data?.queryResult || []}
              columns={columns}
              rowCount={data?.totalRecords || 0}
              loading={loading}
              paginationModel={paginationModel}
              onPaginationModelChange={onPaginationModelChange}
              onSortModelChange={handleSortModelChange}
            />
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

export default TimeOff;
